import { registerPlugin } from '@capacitor/core';

/**
 * Read-only view of the DEVICE STORE ACCOUNT's active subscriptions for this
 * app — the phone's signed-in Apple ID (StoreKit 2 `Transaction.currentEntitlements`)
 * or Google Play account (Play Billing `queryPurchasesAsync`). Independent of
 * the current Firebase/RevenueCat uid, which is the whole point: it answers
 * "does the person holding this phone already pay for this app?" even after a
 * reinstall, cleared storage, or a sign-in to a different account.
 *
 * STRICTLY read-only and side-effect-free: no purchase, no restore, no receipt
 * re-post, no RevenueCat transfer. RevenueCat remains the only thing that
 * purchases or restores — this query is a HINT for which surface to lead with
 * (Restore vs a fresh sell), never a grant. Entitlement derivation stays in
 * the single source of truth (@shared/constants/entitlements.ts); this plugin
 * reports raw store product ids only.
 */
export interface StoreEntitlementsResult {
  /**
   * False when the platform can't answer (web, Play Billing unavailable,
   * pre-iOS-15). Callers must treat unsupported as UNKNOWN — fall back to
   * existing behavior — never as "owns nothing".
   */
  supported: boolean;
  /**
   * Store product ids (e.g. "spending_tools_monthly") of ACTIVE subscriptions
   * the device's store account holds for this app. Both underlying queries are
   * app-scoped and exclude expired/revoked subs; a cancelled-but-not-yet-expired
   * sub still appears (it's still active — restore recovers it honestly).
   */
  productIds: string[];
}

export interface StoreEntitlementsPlugin {
  getStoreEntitlements(): Promise<StoreEntitlementsResult>;
}

const StoreEntitlements = registerPlugin<StoreEntitlementsPlugin>('StoreEntitlements', {
  web: () => Promise.resolve({
    getStoreEntitlements: async () => ({ supported: false, productIds: [] }),
  }),
});

export default StoreEntitlements;
